"use client";

import { AuthBindings } from "@refinedev/core";
import Cookies from "js-cookie";

const ACCESS_TOKEN_KEY = "ATPM";
const REFRESH_TOKEN_KEY = "rt";
const API_URL = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:3000";

let isRefreshing = false;
let failedQueue: Array<{
  resolve: () => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve();
    }
  });
  failedQueue = [];
};

const refreshAuth = async (): Promise<boolean> => {
  try {
    const refreshToken = Cookies.get(REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      throw new Error("Token refresh failed");
    }

    const data = await response.json();
    Cookies.set(ACCESS_TOKEN_KEY, data.access_token, {
      expires: new Date(Date.now() + 12 * 60 * 60 * 1000),
    });
    Cookies.set(REFRESH_TOKEN_KEY, data.refresh_token, {
      expires: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000),
    });

    return true;
  } catch (error) {
    Cookies.remove(ACCESS_TOKEN_KEY);
    Cookies.remove(REFRESH_TOKEN_KEY);
    throw error;
  }
};

const addToQueue = (): Promise<void> =>
  new Promise((resolve, reject) => {
    failedQueue.push({ resolve, reject });
  });

export const handleAuthError = async (): Promise<void> => {
  if (isRefreshing) {
    return addToQueue();
  }

  isRefreshing = true;

  try {
    await refreshAuth();
    isRefreshing = false;
    processQueue(null);
  } catch (error) {
    isRefreshing = false;
    processQueue(error);
    throw error;
  }
};

export const authProvider: AuthBindings = {
  login: async ({ email, password }) => {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        return {
          success: false,
          error: {
            name: "LoginError",
            message: "Invalid username or password",
          },
        };
      }

      const data = await response.json();
      Cookies.set(ACCESS_TOKEN_KEY, data.accessToken);
      Cookies.set(REFRESH_TOKEN_KEY, data.refreshToken);

      return {
        success: true,
        redirectTo: "/",
      };
    } catch (error) {
      return {
        success: false,
        error: {
          name: "LoginError",
          message: "An error occurred during login",
        },
      };
    }
  },

  logout: async () => {
    const token = Cookies.get(ACCESS_TOKEN_KEY);
    if (token) {
      try {
        const response = await fetch(`${API_URL}/auth/logout`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Logout failed");
        }

        const data = await response.json();

        // Clean up tokens
        Cookies.remove(ACCESS_TOKEN_KEY);
        Cookies.remove(REFRESH_TOKEN_KEY);

        // Redirect to Auth0 logout URL
        if (data.logoutURL) {
          window.location.href = data.logoutURL;
          return {
            success: true,
            redirectTo: "/login",
          };
        }
      } catch (error) {
        console.error("Logout error:", error);
      } finally {
        Cookies.remove(ACCESS_TOKEN_KEY);
        Cookies.remove(REFRESH_TOKEN_KEY);
      }
    }

    return {
      success: true,
      redirectTo: "/login",
    };
  },

  check: async () => {
    const token = Cookies.get(ACCESS_TOKEN_KEY);
    if (token) {
      return {
        authenticated: true,
      };
    }

    if (Cookies.get(REFRESH_TOKEN_KEY)) {
      try {
        await refreshAuth();
        return {
          authenticated: true,
        };
      } catch (error) {
        return {
          authenticated: false,
          logout: true,
          redirectTo: "/login",
        };
      }
    }

    console.log("Logging out on client side");

    return {
      authenticated: false,
      logout: true,
      redirectTo: "/login",
    };
  },

  getPermissions: async () => {
    const token = Cookies.get(ACCESS_TOKEN_KEY);
    if (token) {
      // You could decode JWT here to get permissions from payload
      // or make an API call to get permissions
      return ["admin"];
    }
    return null;
  },

  getIdentity: async () => {
    const token = Cookies.get(ACCESS_TOKEN_KEY);
    if (token) {
      const payload = decodeJwtPayload(token);
      if (payload) {
        return {
          id: payload.sub ?? payload.user_id ?? payload.uid,
          email: payload.email,
          name: payload.name ?? payload.email,
        };
      }
    }
    return null;
  },

  onError: async (error) => {
    if (error.statusCode === 401) {
      try {
        await handleAuthError();
        return { error: null };
      } catch (refreshError) {
        return { logout: true };
      }
    }

    return { error };
  },
};

// Helper to get the current access token
export const getAccessToken = () => Cookies.get(ACCESS_TOKEN_KEY);

type JwtPayload = {
  sub?: string;
  email?: string;
  name?: string;
  user_id?: string;
  uid?: string;
  [key: string]: unknown;
};

export const decodeJwtPayload = (token: string): JwtPayload | null => {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "=",
    );
    const json = decodeURIComponent(
      atob(padded)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
};
